import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Checkbox, Grid, IconCaretRight, Text } from '@vtex/admin-ui'
import { type IntlMessageKeys } from '@/components/IntlProvider'
import { type FieldState } from '@/components/products-listing/ExportProductAndSKUDrawer'
import { stopClickPropagation } from '@/lib/middleware'

import * as styles from './ExportFields.styles'

interface ExportFieldsProps {
  isProduct: boolean
  currentSelectedFields?: string[]
  selectedFields: string[]
  setSelectedFields: (
    fields: string[] | ((prevFields: string[]) => string[])
  ) => void
  fieldState: FieldState
  setFieldState: (fieldState: FieldState) => void
}

export const ExportFields = ({
  isProduct,
  selectedFields,
  setSelectedFields,
  fieldState,
  setFieldState,
}: ExportFieldsProps) => {
  const { formatMessage } = useIntl()
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((value) => !value)
  }, [])

  const leftAction = useMemo(() => {
    return (
      <Button
        className={`${styles.expandButton} ${isExpanded ? styles.rotateIcon : ''}`}
        onClick={stopClickPropagation(toggleIsExpanded)}
        variant="neutralTertiary"
        icon={<IconCaretRight />}
        aria-label={formatMessage({
          id: isProduct
            ? 'admin/admin.catalog.export-drawer.select-product-fields'
            : 'admin/admin.catalog.export-drawer.select-sku-fields',
          defaultMessage: isProduct
            ? 'Show product fields'
            : 'Show SKUs fields',
        })}
      />
    )
  }, [formatMessage, isExpanded, isProduct, toggleIsExpanded])

  const SKUFieldsList = useMemo(
    () => [
      { name: 'SKU ID', value: 'SkuId', required: true },
      { name: 'SKU name', value: 'SkuName', required: true },
      { name: 'Activate SKU if possible', value: 'ActivateSkuIfPossible' },
      { name: 'Active SKU', value: 'SkuIsActive' },
      { name: 'Bundle', value: 'SkuKit' },
      { name: 'SKU reference code', value: 'SkuRefId' },
      { name: 'EAN/UPC', value: 'EAN' },
      { name: 'Manufacturer code', value: 'ManufacturerCode' },
      { name: 'Package weight', value: 'Weight' },
      { name: 'Package width', value: 'Width' },
      { name: 'Package height', value: 'Height' },
      { name: 'Package length', value: 'Length' },
      { name: 'Actual weight', value: 'RealWeight' },
      { name: 'Actual width', value: 'RealWidth' },
      { name: 'Actual height', value: 'RealHeight' },
      { name: 'Actual length', value: 'RealLength' },
      { name: 'Cubic weight', value: 'CubicWeight' },
      { name: 'Unit of measure', value: 'MeasurementUnit' },
      { name: 'Unit multiplier', value: 'UnitMultiplier' },
      { name: 'Commercial condition', value: 'CommercialCondition' },
      { name: 'Loyalty amount', value: 'RewardValue' },
      { name: 'Presale date', value: 'EstimatedDateArrival' },
      { name: 'Attachments', value: 'Attachments' },
      { name: 'Accessories', value: 'Accessories' },
      { name: 'Suggestions', value: 'Suggestions' },
      { name: 'Similar products', value: 'Similar' },
      { name: 'Show together', value: 'ShowTogether' },
    ],
    []
  )

  const productFieldsList = useMemo(
    () => [
      { name: 'Product ID', value: 'ProductId', required: true },
      { name: 'Product name', value: 'ProductName', required: true },
      { name: 'Active product', value: 'ProductIsActive' },
      { name: 'Description', value: 'Description' },
      { name: 'Additional Description', value: 'DescriptionShort' },
      { name: 'Brand ID', value: 'BrandId' },
      { name: 'Brand', value: 'BrandName' },
      { name: 'Department ID', value: 'DepartmentId' },
      { name: 'Department', value: 'DepartmentName' },
      { name: 'Category ID', value: 'CategoryId' },
      { name: 'Category', value: 'CategoryName' },
      { name: 'Sales channels', value: 'SalesChannel' },
      { name: 'Global category ID', value: 'GlobalCategoryId' },
      { name: 'Global category', value: 'GlobalCategoryName' },
      { name: 'Product URL', value: 'ProductUrl' },
      { name: 'Page title', value: 'Title' },
      { name: 'Meta description', value: 'MetaTagDescription' },
      { name: 'Display on website', value: 'IsVisible' },
      { name: 'Show when out of stock', value: 'ShowWithoutStock' },
      { name: 'Release Date', value: 'ReleaseDate' },
      { name: 'Substitute words', value: 'Keywords' },
      { name: 'Product reference code', value: 'ProductRefId' },
      { name: 'Tax code', value: 'TaxCode' },
    ],
    []
  )

  const fieldsList = useMemo(
    () => (isProduct ? productFieldsList : SKUFieldsList),
    [isProduct, productFieldsList, SKUFieldsList]
  )

  useEffect(() => {
    setSelectedFields(fieldsList.map((field) => field.value))
  }, [fieldsList, setSelectedFields])

  const checkboxValue = useMemo(() => {
    if (!fieldsList.length) {
      return false
    }

    if (fieldsList.every((field) => selectedFields.includes(field.value))) {
      return true
    }

    if (fieldsList.some((field) => selectedFields.includes(field.value))) {
      return 'indeterminate'
    }
    return false
  }, [selectedFields, fieldsList])

  const checkboxRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (checkboxRef?.current?.name === 'product-fields') {
      setFieldState({ product: checkboxValue as string, sku: fieldState.sku })
    } else if (checkboxRef?.current?.name === 'sku-fields') {
      setFieldState({
        product: fieldState.product,
        sku: checkboxValue as string,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkboxValue])

  const handleCheckboxChange = (fieldValue: string, isChecked: boolean) => {
    setSelectedFields((prevSelectedFields: string[]) => {
      if (fieldValue === 'product-fields' || fieldValue === 'sku-fields') {
        return isChecked
          ? fieldsList.map((field) => field.value)
          : fieldsList
              .filter((field) => field.required)
              .map((field) => field.value)
      }
      if (isChecked) {
        return [...prevSelectedFields, fieldValue]
      } else {
        return prevSelectedFields.filter(
          (value: string) => value !== fieldValue
        )
      }
    })
  }

  return (
    <>
      <label>
        <Grid
          templateColumns="24px 1fr 24px"
          columnGap="8px"
          className={`${styles.wrapper}`}
        >
          {leftAction}
          <Text variant="title1" tone="secondary">
            <FormattedMessage
              id={
                isProduct
                  ? 'admin/admin.catalog.export-drawer.product-fields-label'
                  : 'admin/admin.catalog.export-drawer.sku-fields-label'
              }
              defaultMessage={isProduct ? 'Product fields' : 'SKU fields'}
            />
          </Text>
          <Checkbox
            ref={checkboxRef}
            name={isProduct ? 'product-fields' : 'sku-fields'}
            className={styles.checkbox}
            checked={checkboxValue === true}
            onChange={(e) =>
              handleCheckboxChange(
                isProduct ? 'product-fields' : 'sku-fields',
                e.target.checked
              )
            }
            {...{
              state: {
                value: checkboxValue,
                setValue: () => undefined,
              },
            }}
          />
        </Grid>
      </label>

      {isExpanded && (
        <>
          {fieldsList.map((field) => (
            <Grid
              key={field.value}
              columnGap="8px"
              className={`${styles.wrapper} ${styles.insideWrapper}`}
            >
              <Checkbox
                name={field.value}
                className={styles.checkbox}
                checked={selectedFields.includes(field.value)}
                disabled={field.required}
                onChange={(e) =>
                  handleCheckboxChange(field.value, e.target.checked)
                }
                label={formatMessage({
                  id: `admin/admin.catalog.export-drawer.${isProduct ? 'product' : 'sku'}-fields.option-${field.value}` as IntlMessageKeys,
                  defaultMessage: field.name,
                })}
              />
            </Grid>
          ))}
        </>
      )}
    </>
  )
}
